/**
 * Shared GLSL (GLSL ES 3.00 via Babylon's WebGL2 shader processor: write `attribute`, `varying`,
 * `gl_FragColor`; `texelFetch`, `textureSize`, `fwidth` are available).
 *
 * Style-owned ShaderMaterials include `GLSL_HUB` and register with `UniformHub` to receive
 * `uPxScale` (backbuffer px per stage px = k / r), `uTime`, `uBackbufferPx`, `uViewportPx`
 * (current camera), `uViewportOrigin` (current camera, bottom-left backbuffer px).
 */

/** Hub uniform declarations plus stage-px helpers (fragment or vertex). */
export const GLSL_HUB = /* glsl */ `
uniform float uPxScale;
uniform float uTime;
uniform vec2 uBackbufferPx;
uniform vec2 uViewportPx;
uniform vec2 uViewportOrigin;
`;

/** Fragment-only helpers: stage px (y down), the stage-px dither grid (09 §6) and a 4×4 Bayer. */
export const GLSL_STAGE_FRAG = /* glsl */ `
// Canvas backbuffer px of this fragment (GL axes, y up).
vec2 ucFragPx() {
  return gl_FragCoord.xy;
}
// Stage px of this fragment, y down from the stage top (stage origin = canvas top-left).
vec2 ucStagePx() {
  vec2 f = ucFragPx();
  return vec2(f.x, uBackbufferPx.y - f.y) / uPxScale;
}
// Backbuffer px per dither cell: one stage px at integer u (09 §6: max(1, round(uPxScale))).
float ucCellSize() {
  return max(1.0, floor(uPxScale + 0.5));
}
// Integer stage-px cell (x right, y down): floor(fragPx / max(1, round(uPxScale))), y from the top.
vec2 ucStageCell() {
  vec2 f = ucFragPx();
  return floor(vec2(f.x, uBackbufferPx.y - f.y) / ucCellSize());
}
// Backbuffer offset from this fragment to the centre of its stage cell (GL axes, y up).
vec2 ucCellCentreOffset() {
  float n = ucCellSize();
  vec2 f = ucFragPx();
  vec2 sp = vec2(f.x, uBackbufferPx.y - f.y);
  vec2 c = (floor(sp / n) + 0.5) * n;
  return vec2(c.x - sp.x, -(c.y - sp.y));
}
// v6 Bayer 4x4: M[sy % 4][sx % 4] / 16 + 1/32 at stage px (sx, sy), y down.
const float UC_BAYER4[16] = float[16](0.0, 8.0, 2.0, 10.0, 12.0, 4.0, 14.0, 6.0, 3.0, 11.0, 1.0, 9.0, 15.0, 7.0, 13.0, 5.0);
float ucBayer4(vec2 cell) {
  int x = int(mod(cell.x, 4.0));
  int y = int(mod(cell.y, 4.0));
  return UC_BAYER4[y * 4 + x] / 16.0 + 1.0 / 32.0;
}
`;
